import React from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { useGamePass } from './GamePassProvider';
import { fetchGoogleUserInfo } from '../utils/googleAuth';
import { GoogleUser } from '../types';

interface AuthFormProps {
  provider: 'google';
  onSuccess?: (user: GoogleUser) => void;
  onError?: (error: Error) => void;
  buttonLabel?: string;
  buttonClassName?: string;
  buttonStyle?: React.CSSProperties;
}

export const AuthForm: React.FC<AuthFormProps> = ({
  provider,
  onSuccess: customOnSuccess,
  onError: customOnError,
  buttonLabel = 'Login with Google',
  buttonClassName = '',
  buttonStyle = {},
}) => {
  const { onSuccess: contextOnSuccess, onError: contextOnError } = useGamePass();

  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const userInfo = await fetchGoogleUserInfo(tokenResponse.access_token);
        
        // Call custom callback first
        customOnSuccess?.(userInfo);
        
        // Then call context callback
        contextOnSuccess?.(userInfo);
      } catch (error) {
        const err = error as Error;
        customOnError?.(err);
        contextOnError?.(err);
      }
    },
    onError: (error) => {
      const err = error as Error;
      customOnError?.(err);
      contextOnError?.(err);
    },
  });

  const defaultButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '12px 24px',
    backgroundColor: '#4285f4',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    ...buttonStyle,
  };

  return (
    <button
      onClick={() => login()}
      className={`gamepass-auth-form-button ${buttonClassName}`}
      style={defaultButtonStyle}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.25)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {provider === 'google' && (
        <svg viewBox="0 0 24 24" width="20" height="20">
          <path
            fill="#ffffff"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#ffffff"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#ffffff"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#ffffff"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
      )}
      {buttonLabel}
    </button>
  );
};